import { useEffect, useId, useRef, useState } from 'react';
import styled from 'styled-components';

export default function LiveProductSelect({ label, value, products, disabled, onChange }: {
  label: string; value: string; products: { seq: number; name: string }[]; disabled: boolean; onChange: (value: string) => void;
}) {
  const id = useId(), root = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false), [query, setQuery] = useState(''), [active, setActive] = useState(0);
  const selected = products.find(product => String(product.seq) === value);
  const matches = products.filter(product => product.name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()));
  useEffect(() => { root.current?.querySelector('.active')?.scrollIntoView?.({ block: 'nearest' }); }, [active, open, query]);
  const choose = (seq: number) => { onChange(String(seq)); setOpen(false); setQuery(''); };
  return <Select ref={root} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setOpen(false); }}>
    <label htmlFor={id}>{label}</label>
    <div className="control"><input id={id} role="combobox" aria-autocomplete="list" aria-expanded={open && !disabled}
      aria-controls={`${id}-list`} aria-activedescendant={open && matches[active] ? `${id}-${matches[active].seq}` : undefined}
      disabled={disabled} value={open ? query : selected?.name || ''} placeholder="상품명 검색 후 선택"
      onFocus={() => { setOpen(true); setQuery(''); setActive(0); }}
      onClick={() => { if (!open) { setOpen(true); setQuery(''); setActive(0); } }}
      onChange={event => { setQuery(event.target.value); setActive(0); setOpen(true); }}
      onKeyDown={event => {
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); setOpen(false); return; }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault(); setOpen(true); setActive(index => open ? Math.max(0, Math.min(matches.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1))) : 0);
        }
        if (event.key === 'Enter' && open) { event.preventDefault(); if (matches[active]) choose(matches[active].seq); }
      }} /><span aria-hidden="true">▾</span></div>
    {open && !disabled && <div className="dropdown"><ul id={`${id}-list`} role="listbox" aria-label={`${label} 검색 결과`}>
      {matches.map((product, index) => <li key={product.seq} id={`${id}-${product.seq}`} role="option" aria-selected={String(product.seq) === value}
        className={index === active ? 'active' : ''} onMouseDown={event => event.preventDefault()} onMouseEnter={() => setActive(index)} onClick={() => choose(product.seq)}>
        {product.name}
      </li>)}
    </ul>{!matches.length && <p role="status">검색 결과가 없습니다.</p>}</div>}
  </Select>;
}

const Select = styled.div`
  position: relative; min-width: 0;
  > label { display: block; margin-bottom: var(--space-2); font-weight: var(--font-weight-semibold); }
  .control { position: relative; }
  && input { box-sizing: border-box; width: 100%; padding-right: var(--space-8); }
  .control > span { position: absolute; right: var(--space-3); top: 50%; transform: translateY(-50%); pointer-events: none; color: var(--color-text-secondary); }
  .dropdown { position: absolute; top: 100%; left: 0; right: 0; z-index: var(--z-dropdown); padding: var(--space-1);
    border: 1px solid var(--color-border-strong); border-radius: var(--radius-md); background: var(--color-bg-elevated); box-shadow: var(--shadow-md); }
  ul { list-style: none; margin: 0; padding: 0; max-height: calc(var(--control-lg) * 4); overflow-y: auto; }
  li { min-height: var(--touch-target); box-sizing: border-box; display: flex; align-items: center; padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm); color: var(--color-text-primary); cursor: pointer; overflow-wrap: anywhere; }
  li.active { background: var(--color-bg-subtle); }
  li[aria-selected='true'] { background: var(--color-brand-subtle); color: var(--color-text-link); font-weight: var(--font-weight-semibold); }
  .dropdown > p { margin: 0; padding: var(--space-3); color: var(--color-text-secondary); }
`;
